// Shared onboarding steps: authenticator set-up and backup codes.

import { useState } from "react";
import { otpauthUri } from "../crypto";
import { copyText, download } from "../lib/clipboard";
import { Button, Field, IconButton, Input } from "../ui/ds";
import { useToast } from "../state/store";
import { QrCode } from "../ui/QrCode";

/** Show a new TOTP secret and collect the first code. */
export function TotpEnrol({
  secret,
  account,
  busy,
  error,
  submitLabel = "Verify",
  onSubmit,
}: {
  secret: string;
  account: string;
  busy: boolean;
  error: string;
  submitLabel?: string;
  onSubmit: (code: string) => void;
}) {
  const [code, setCode] = useState("");
  const toast = useToast();
  const grouped = secret.match(/.{1,4}/g)?.join(" ") ?? secret;
  return (
    <form
      className="gate-stack"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(code);
      }}
    >
      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7, color: "var(--text-body)" }}>
        <li>Open your authenticator app (1Password, Google Authenticator, Authy…).</li>
        <li>Scan this QR code, or choose to enter a setup key and type the key below.</li>
        <li>Enter the 6-digit code it shows.</li>
      </ol>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <QrCode value={otpauthUri(secret, account)} label="QR code for your authenticator app" />
      </div>
      <div className="copy-box">
        <span className="secret-key" style={{ flex: 1 }}>
          {grouped}
        </span>
        <IconButton
          icon="copy"
          label="Copy setup key"
          variant="ghost"
          size="sm"
          onClick={() => void copyText(secret, false).then((ok) => ok && toast("Setup key copied"))}
        />
      </div>
      <a href={otpauthUri(secret, account)} style={{ fontSize: 13, fontWeight: 600 }}>
        On this phone? Open it in your authenticator app
      </a>
      <Field label="Authenticator code" error={error}>
        <Input
          size="lg"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000 000"
          value={code}
          mono
          onChange={(v) => setCode(v.replace(/[^\d ]/g, "").slice(0, 7))}
        />
      </Field>
      <Button type="submit" fullWidth arrow busy={busy}>
        {busy ? "Checking…" : submitLabel}
      </Button>
    </form>
  );
}

/** Show the ten backup codes with copy/download, and ask the person to confirm. */
export function BackupCodesPanel({
  codes,
  used = [],
  email,
}: {
  codes: string[];
  used?: number[];
  email: string;
}) {
  const toast = useToast();
  return (
    <div className="gate-stack">
      <div className="codes-grid">
        {codes.map((c, i) => (
          <div key={c + i} className={used.includes(i) ? "is-used" : ""}>
            {c}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button
          variant="secondary"
          size="sm"
          icon="copy"
          onClick={() => void copyText(codes.join("\n"), false).then((ok) => ok && toast("Backup codes copied", "Paste them somewhere other than this vault."))}
        >
          Copy
        </Button>
        <Button variant="secondary" size="sm" icon="download" onClick={() => downloadCodes(codes, email, toast)}>
          Download
        </Button>
      </div>
    </div>
  );
}

export function downloadCodes(codes: string[], email: string, toast: (t: string, m?: string) => void) {
  download(
    "agile-vault-backup-codes.txt",
    `Agile Vault backup codes\n${email}\n\n${codes.join("\n")}\n\nEach code works once.\n`,
  );
  toast("Backup codes downloaded", "agile-vault-backup-codes.txt · keep it somewhere other than this vault.");
}
